import { useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import type { Membership } from "../lib/chosen-workspace.js";
import { initialsOf } from "../lib/initials.js";
import { queryKeys } from "../lib/query-keys.js";
import { Check, Plus } from "./icons.js";
import { NewWorkspace } from "./new-workspace.js";
import "./rail-workspace.css";

/**
 * Every workspace this account is in, the current one ticked, then "New
 * workspace" (DESIGN.md §9, "The workspace switcher").
 *
 * Rows only. Two menus draw them — the workspace menu in the rail, and setup's
 * switcher where there is no rail — and each decides what sits around them.
 */
export function WorkspaceChoices({
  current,
  workspaces,
  onChoose,
  onNew,
}: {
  readonly current: string;
  readonly workspaces: readonly Membership[];
  /** Called for a workspace other than the current one, after the menu closes. */
  readonly onChoose: (workspaceId: string) => void;
  readonly onNew: () => void;
}) {
  return (
    <>
      {workspaces.map((workspace) => (
        <button
          key={workspace.workspaceId}
          type="button"
          className="account-menu-item"
          aria-current={workspace.workspaceId === current ? "true" : undefined}
          onClick={() => {
            onChoose(workspace.workspaceId);
          }}
        >
          <span className="rail-workspace-menu-tile" aria-hidden="true">
            {initialsOf(workspace.workspaceName)}
          </span>
          <span className="rail-workspace-menu-name">{workspace.workspaceName}</span>
          {workspace.workspaceId === current ? (
            <Check className="account-menu-icon" aria-hidden="true" />
          ) : null}
        </button>
      ))}
      <button type="button" className="account-menu-item" onClick={onNew}>
        <Plus className="account-menu-icon" aria-hidden="true" />
        New workspace
      </button>
    </>
  );
}

/**
 * The "New workspace" dialog, held by whoever owns the menu rather than by the
 * menu itself: the menu closes when the dialog opens, and a dialog living
 * inside it would close with it.
 */
export function useNewWorkspace(onChoose: (workspaceId: string) => void): {
  readonly start: () => void;
  readonly dialog: ReactNode;
} {
  const cache = useQueryClient();
  const [naming, setNaming] = useState(false);
  return {
    start: () => {
      setNaming(true);
    },
    dialog: naming ? (
      <NewWorkspace
        onClose={() => {
          setNaming(false);
        }}
        onMade={async (workspaceId) => {
          // The session's list first, so the switcher can find what it opens.
          await cache.invalidateQueries({ queryKey: queryKeys.me() });
          setNaming(false);
          onChoose(workspaceId);
        }}
      />
    ) : null,
  };
}
